import { Injectable } from '@angular/core';
import {
  ListPageableRequestParams,
  TrackingRegistryService,
} from 'src/generated-sources/openapi/wallet-service/tracking-registry';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { map, Observable } from 'rxjs';
import { PageLoaderService } from 'src/app/services/page-loader.service';
import { HttpResponse } from '@angular/common/http';
import {
  TrackingRegistryBaseFacade,
  TrackingRegistryViewBase,
  RestrictiveData,
} from 'src/app/shared/services/tracking-registry-base.facade';
import { ListFilters } from './list-filter.service';
import { removeSpecialCharacters } from 'src/app/helpers/string.utils';
import { validateBrazilianDocument } from 'src/app/helpers/documents-utils';
import { MAX_PAGE_SIZE } from 'src/app/components/constants';
import { isAlert30DaysWallet } from 'src/app/helpers/plan-utils';
import { Wallet } from 'src/app/services/models/wallet-service/wallet.model';
import { toEndOfDayLocalISO, toStartOfDayLocalISO } from 'src/app/helpers/date-utils';

export type { RestrictiveData };
export interface TrackingRegistryView extends TrackingRegistryViewBase {}

@Injectable()
export class ListIndividualFacade extends TrackingRegistryBaseFacade {
  constructor(
    trackingRegistryService: TrackingRegistryService,
    pageLoaderService: PageLoaderService,
    private readonly walletDataService: WalletDataService,
    private readonly globalService: GlobalServiceService,
  ) {
    super(trackingRegistryService, pageLoaderService);
  }

  public getTrackingRegistries(
    params: ListPageableRequestParams,
  ): Observable<{ items: TrackingRegistryView[]; total: number }> {
    return this.trackingRegistryService.listPageable(params).pipe(
      map((response) => ({
        items: response.items.map((dto) => this.transformToView(dto)),
        total: response.total,
      })),
    );
  }

  public buildSearchParams(
    filterValues?: ListFilters,
    page: number = 0,
    walletId: string | undefined = undefined,
    pageSize: number = 10,
  ): ListPageableRequestParams {
    const { document, name, scoreRange, restrictivesRange, dateRange, segment, internalCode } =
      filterValues || {};

    let validDocument: string | undefined = undefined;
    if (document) {
      const validationResult = validateBrazilianDocument(document);
      if (validationResult.isValid) {
        validDocument = removeSpecialCharacters(document);
      }
    }

    const params: ListPageableRequestParams = {
      scoreMin: scoreRange?.min,
      scoreMax: scoreRange?.max,
      restritivosMin: restrictivesRange?.min,
      restritivosMax: restrictivesRange?.max,
      dateStart: toStartOfDayLocalISO(dateRange?.start),
      dateEnd: toEndOfDayLocalISO(dateRange?.end),
      segment: segment ? segment.trim() : undefined,
      internalCode: internalCode ? internalCode.trim() : undefined,
      name: name ? removeSpecialCharacters(name) : undefined,
      document: validDocument,
      walletId: walletId,
      page: page,
      perPage: pageSize,
    };

    return params;
  }

  public canDownloadReport(totalRegistries: number): boolean {
    return totalRegistries > 0 && totalRegistries <= 1000;
  }

  public downloadReport(params: ListPageableRequestParams): Observable<HttpResponse<Blob>> {
    const reportParams = { ...params, perPage: MAX_PAGE_SIZE, page: 0 };
    return this.trackingRegistryService.listAllReportXlsx(reportParams, 'response');
  }

  public getWalletById(walletId: string) {
    return this.walletDataService.getWalletById(walletId);
  }

  public getWallets() {
    return this.walletDataService.getWallets$();
  }

  public getPersonType() {
    return this.globalService.getPersonType$();
  }

  public getPersonTypeCurrentValue() {
    return this.globalService.getPersonTypeCurrentValue();
  }

  public resolveIsAlert30DaysWallet(walletId: string): boolean {
    const wallet = this.walletDataService.getWalletById(walletId);
    return isAlert30DaysWallet(wallet?.planId);
  }

  public getWalletsExcludingAlert30Days(): Wallet[] {
    return this.walletDataService.getWalletsValue().filter((w) => !isAlert30DaysWallet(w.planId));
  }

  public getDocumentAdded$() {
    return this.globalService.getDocumentAdded$();
  }
}
